import type {
  CreateEndpointRequest,
  UpdateEndpointRequest,
  ImportEndpointsRequest,
  CaseEndpoint,
  CreateRelationshipRequest,
  EndpointRelationship,
  UpdateRelationshipRequest,
  BulkCreateRelationshipRequest,
  BulkCreateResponse,
  MoveRelationshipRequest,
} from '../../../types/case-container'
import {
  bulkCreateRelationships,
  copyRelationships,
  moveRelationship,
} from '../../../services/endpoint-relationships'
import { logger } from '../../../utils/logger'

interface UseCaseInfrastructureHandlersArgs {
  caseId: string
  editingEndpoint: CaseEndpoint | null
  editingRelationship: EndpointRelationship | null
  copyLinksTargetEndpoint: CaseEndpoint | null
  setShowCreateModal: (v: boolean) => void
  setEditingEndpoint: (v: CaseEndpoint | null) => void
  setLinkingEndpoint: (v: CaseEndpoint | null) => void
  setEditingRelationship: (v: EndpointRelationship | null) => void
  setBulkLinkSourceEndpoint: (v: CaseEndpoint | null) => void
  setCopyingLinks: (v: boolean) => void
  setCopyError: (v: string | null) => void
  setMovingRelationship: (v: boolean) => void
  create: (caseId: string, data: CreateEndpointRequest) => Promise<unknown>
  update: (caseId: string, id: string, data: UpdateEndpointRequest) => Promise<unknown>
  createRelationship: (caseId: string, data: CreateRelationshipRequest) => Promise<unknown>
  removeRelationship: (caseId: string, id: string) => Promise<boolean>
  importBulk: (
    caseId: string,
    data: ImportEndpointsRequest
  ) => Promise<{
    imported: number
    skipped: number
    errors: { row: number; field: string; message: string }[]
  } | null>
  refetch: () => unknown
  refetchRelationships: () => unknown
}

export interface CaseInfrastructureHandlers {
  handleCreate: (data: CreateEndpointRequest) => Promise<void>
  handleUpdate: (data: UpdateEndpointRequest) => Promise<void>
  handleCreateRelationship: (data: CreateRelationshipRequest) => Promise<void>
  handleImport: (data: ImportEndpointsRequest) => Promise<{
    imported: number
    skipped: number
    errors: { row: number; field: string; message: string }[]
  } | null>
  handleUpdateRelationship: (_data: UpdateRelationshipRequest) => void
  handleDeleteRelationship: (relationshipId: string) => Promise<void>
  handleBulkCreateRelationships: (
    data: BulkCreateRelationshipRequest
  ) => Promise<BulkCreateResponse | null>
  handleCopyLinks: (fromEndpointId: string) => Promise<BulkCreateResponse | null>
  handleMoveRelationship: (
    relationshipId: string,
    request: MoveRelationshipRequest
  ) => Promise<void>
}

type EndpointHandlers = Pick<
  CaseInfrastructureHandlers,
  'handleCreate' | 'handleUpdate' | 'handleImport'
>

function useEndpointHandlers(args: UseCaseInfrastructureHandlersArgs): EndpointHandlers {
  const {
    caseId,
    editingEndpoint,
    setShowCreateModal,
    setEditingEndpoint,
    create,
    update,
    importBulk,
    refetch,
  } = args

  const handleCreate = async (data: CreateEndpointRequest): Promise<void> => {
    const result = await create(caseId, data)
    if (result) {
      setShowCreateModal(false)
      void refetch()
    }
  }

  const handleUpdate = async (data: UpdateEndpointRequest): Promise<void> => {
    if (!editingEndpoint) return
    const result = await update(caseId, editingEndpoint.id, data)
    if (result) {
      setEditingEndpoint(null)
      void refetch()
    }
  }

  const handleImport: CaseInfrastructureHandlers['handleImport'] = async (data) =>
    importBulk(caseId, data)

  return { handleCreate, handleUpdate, handleImport }
}

type RelationshipHandlers = Pick<
  CaseInfrastructureHandlers,
  | 'handleCreateRelationship'
  | 'handleUpdateRelationship'
  | 'handleDeleteRelationship'
  | 'handleBulkCreateRelationships'
  | 'handleCopyLinks'
  | 'handleMoveRelationship'
>

interface CopyLinksDeps {
  caseId: string
  copyLinksTargetEndpoint: CaseEndpoint | null
  setCopyingLinks: (v: boolean) => void
  setCopyError: (v: string | null) => void
  refetchRelationships: () => unknown
}

function createCopyLinksHandler(
  deps: CopyLinksDeps,
): CaseInfrastructureHandlers['handleCopyLinks'] {
  return async (fromEndpointId) => {
    const { caseId, copyLinksTargetEndpoint, setCopyingLinks, setCopyError, refetchRelationships } =
      deps
    if (!copyLinksTargetEndpoint) return null
    setCopyingLinks(true)
    setCopyError(null)
    try {
      const result = await copyRelationships(caseId, {
        fromEndpointId,
        toEndpointId: copyLinksTargetEndpoint.id,
      })
      void refetchRelationships()
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to copy links'
      setCopyError(message)
      logger.error('CaseInfrastructureTab', 'Copy links failed:', err)
    } finally {
      setCopyingLinks(false)
    }
    return null
  }
}

interface MoveRelationshipDeps {
  caseId: string
  setMovingRelationship: (v: boolean) => void
  setEditingRelationship: (v: EndpointRelationship | null) => void
  refetchRelationships: () => unknown
}

function createMoveRelationshipHandler(
  deps: MoveRelationshipDeps,
): (relationshipId: string, request: MoveRelationshipRequest) => Promise<void> {
  return async (relationshipId, request) => {
    const { caseId, setMovingRelationship, setEditingRelationship, refetchRelationships } = deps
    setMovingRelationship(true)
    try {
      await moveRelationship(caseId, relationshipId, request)
      setEditingRelationship(null)
      void refetchRelationships()
    } catch (err) {
      logger.error('CaseInfrastructureTab', 'Move relationship failed:', err)
      throw err
    } finally {
      setMovingRelationship(false)
    }
  }
}

function useRelationshipHandlers(args: UseCaseInfrastructureHandlersArgs): RelationshipHandlers {
  const {
    caseId,
    editingRelationship,
    copyLinksTargetEndpoint,
    setLinkingEndpoint,
    setEditingRelationship,
    setBulkLinkSourceEndpoint,
    setCopyingLinks,
    setCopyError,
    setMovingRelationship,
    createRelationship,
    removeRelationship,
    refetchRelationships,
  } = args

  const handleCreateRelationship = async (data: CreateRelationshipRequest): Promise<void> => {
    const result = await createRelationship(caseId, data)
    if (result) {
      setLinkingEndpoint(null)
      void refetchRelationships()
    }
  }

  const handleUpdateRelationship: CaseInfrastructureHandlers['handleUpdateRelationship'] = (
    _data
  ) => {
    if (!editingRelationship) return
    setEditingRelationship(null)
    void refetchRelationships()
  }

  const handleDeleteRelationship = async (relationshipId: string): Promise<void> => {
    const success = await removeRelationship(caseId, relationshipId)
    if (success) void refetchRelationships()
  }

  const handleBulkCreateRelationships: CaseInfrastructureHandlers['handleBulkCreateRelationships'] =
    async (data) => {
      try {
        const result = await bulkCreateRelationships(caseId, data)
        setBulkLinkSourceEndpoint(null)
        void refetchRelationships()
        return result
      } catch (err) {
        logger.error('CaseInfrastructureTab', 'Bulk create failed:', err)
      }
      return null
    }

  const handleCopyLinks = createCopyLinksHandler({
    caseId,
    copyLinksTargetEndpoint,
    setCopyingLinks,
    setCopyError,
    refetchRelationships,
  })

  const handleMoveRelationship = createMoveRelationshipHandler({
    caseId,
    setMovingRelationship,
    setEditingRelationship,
    refetchRelationships,
  })

  return {
    handleCreateRelationship,
    handleUpdateRelationship,
    handleDeleteRelationship,
    handleBulkCreateRelationships,
    handleCopyLinks,
    handleMoveRelationship,
  }
}

export function useCaseInfrastructureHandlers(
  args: UseCaseInfrastructureHandlersArgs
): CaseInfrastructureHandlers {
  const endpointHandlers = useEndpointHandlers(args)
  const relationshipHandlers = useRelationshipHandlers(args)
  return { ...endpointHandlers, ...relationshipHandlers }
}
